import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { Autocomplete } from 'components/FormFields';
import { locationActions } from 'state/modules/locations';
import { truncateKeywordSpaces } from 'utils/utils';

const LocationsSelect = ({
  label,
  variant,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  customParams,
  disabled,
  margin,
  onChange,
}) => {
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    keyword: value,
    ...customParams,
  });

  const { loading, locations } = useSelector((state) => state.locations);

  const fetchLocations = useCallback(
    debounce((params) => {
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(locationActions.fetchLocations(newParams));
    }, 1000),
    []
  );

  useEffect(() => {
    if (!value) return;
    fetchLocations(params);
  }, []);

  const mappedValue = locations?.find((loc) => loc.id === value) || '';

  return (
    <Autocomplete
      name={name}
      label={label}
      openOnFocus
      margin={margin}
      fullwidth="true"
      loading={loading}
      variant={variant}
      options={locations}
      disabled={disabled}
      clearOnBlur={false}
      value={mappedValue}
      id="locations-select"
      data-cy="locations-selector"
      getOptionLabel={(option) => option.name || ''}
      onChange={(ev, val) => {
        if (onChange) {
          onChange(val);
        }
        setFieldValue(name, val?.id || '');
      }}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option.id === value.id}
      onInputChange={(_, val) => {
        if (val != mappedValue?.name) {
          fetchLocations({ ...params, keyword: val });
        }
      }}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
    />
  );
};

LocationsSelect.propTypes = {
  label: PropTypes.string,
  margin: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  onChange: PropTypes.func,
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

LocationsSelect.defaultProps = {
  required: true,
  disabled: false,
  margin: 'normal',
  form: {
    errors: {},
    touched: {},
  },
  customParams: {},
  variant: 'standard',
  label: 'Select...',
};

export default LocationsSelect;
